import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ClockIcon, 
  ExclamationTriangleIcon, 
  ArrowLeftIcon, 
  FlagIcon, 
  BookmarkIcon, 
  PaperAirplaneIcon 
} from '@heroicons/react/24/outline'

const TakeExam: React.FC = () => {
  const { examId } = useParams<{ examId: string }>()
  const navigate = useNavigate()
  
  const [timeRemaining, setTimeRemaining] = useState(1800) // 30分钟
  const [currentQuestion, setCurrentQuestion] = useState(1)
  const [totalQuestions] = useState(20)
  
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 移动端固定头部 */}
      <div className="sticky top-0 z-20 bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* 标题和进度 */}
            <div className="flex-1 min-w-0">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">JavaScript 基础知识测试</h1>
              <p className="text-xs sm:text-sm text-gray-600">第 {currentQuestion} 题，共 {totalQuestions} 题</p>
            </div>
            
            {/* 时间和操作 */}
            <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-6">
              <div className="flex items-center px-3 py-1.5 bg-red-50 rounded-lg">
                <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-1.5" />
                <span className="text-sm sm:text-lg font-mono font-bold text-red-600">{formatTime(timeRemaining)}</span>
              </div>
              <button
                onClick={() => navigate(`/student/exams/${examId}`)}
                className="flex items-center px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">暂停退出</span>
                <span className="sm:hidden">退出</span>
              </button>
            </div>
          </div>
          
          {/* 进度条 */}
          <div className="mt-3">
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentQuestion / totalQuestions) * 100}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 flex-shrink-0">{Math.round((currentQuestion / totalQuestions) * 100)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* 主内容区 - 考虑底部导航栏的高度 */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-6 pb-24 sm:pb-6">
        {/* 题目内容 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-4">
          {/* 题目标签 */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="px-2.5 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
              数据类型
            </span>
            <span className="px-2.5 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded-full">
              5分
            </span>
            <span className="px-2.5 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
              简单
            </span>
            <button className="ml-auto p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition">
              <BookmarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* 题目文本 */}
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-5 sm:mb-6">
            以下哪个不是JavaScript的数据类型？
          </h3>

          {/* 选择题选项 - 优化移动端触摸体验 */}
          <div className="space-y-2 sm:space-y-3">
            {['string', 'boolean', 'float', 'undefined'].map((option, index) => (
              <label 
                key={index} 
                className="flex items-center p-3.5 sm:p-4 border-2 rounded-xl hover:bg-gray-50 cursor-pointer transition-all active:scale-[0.99] border-gray-200 hover:border-blue-200"
              >
                <input
                  type="radio"
                  name="question-1"
                  value={index}
                  className="w-5 h-5 mr-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm sm:text-base text-gray-900">{option}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* 底部导航 - 移动端固定底部 */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg sm:relative sm:shadow-none sm:border-none">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <button className="flex-1 sm:flex-initial px-4 py-2.5 sm:px-6 sm:py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 disabled:opacity-50 text-sm font-medium transition">
              上一题
            </button>
            
            <div className="flex gap-2 sm:gap-4 flex-1 sm:flex-initial justify-end">
              <button className="flex-1 sm:flex-initial px-4 py-2.5 sm:px-6 sm:py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 text-sm font-medium transition">
                下一题
              </button>
              <button className="hidden sm:flex items-center px-6 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-medium transition">
                <PaperAirplaneIcon className="w-4 h-4 mr-2" />
                提交考试
              </button>
            </div>
          </div>
          
          {/* 移动端提交按钮 - 单独一行 */}
          <button className="sm:hidden flex items-center justify-center w-full mt-2 px-6 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-medium transition">
            <PaperAirplaneIcon className="w-4 h-4 mr-2" />
            提交考试
          </button>
        </div>
      </div>
    </div>
  )
}

export default TakeExam
